/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {CreateBacklog, DeleteBacklog, RenameBacklog} from "./backlog";
import {CompleteWorkitem, CreateWorkitem, DeleteWorkitem, RenameWorkitem} from "./workitem";
import {Origin, StrategyType} from "./abstract_stragegy";
import {Root, Error_} from "./root";
import {Connection, Pong} from "./connection";
import {adminUser} from "./abstract_data_item";
import {decrypt, encrypt, useEncryption} from "./encryption_utils";

const re = /([1-9][0-9]*)\s*,\s*([0-9: .\-+]+)\s*,\s*([\w\-.]+@(?:[\w-]+\.)+[\w-]{2,4})\s*:\s*([a-zA-Z]+)\s*\(\s*"((?:[^"\\]|\\"|\\\\)*)?"\s*(?:,\s*"((?:[^"\\]|\\"|\\\\)*)")?\s*(?:,\s*"((?:[^"\\]|\\"|\\\\)*)"\s*)*\)/;
const strategies = new Map<String, StrategyType>([
    ['CreateBacklog', CreateBacklog],
    ['CreateWorkitem', CreateWorkitem],
    ['RenameWorkitem', RenameWorkitem],
    ['DeleteBacklog', DeleteBacklog],
    ['DeleteWorkitem', DeleteWorkitem],
    ['RenameBacklog', RenameBacklog],
    ['CompleteWorkitem', CompleteWorkitem],
    ['Pong', Pong],
    ['Error', Error_],
]);

export async function execute(data: Root, connection: Connection, s: string): Promise<boolean> {
    let isEncrypted = false;

    if (s.trim().length === 0 || s.startsWith('#')) {
        return false;
    }
    else if(s.startsWith('+')) {
        // check if encrypted
        isEncrypted = true;
        const token = s;
        s = await decrypt(token);
    }
    if (s === 'ReplayCompleted()') {
        data.isLoggedIn = true;
        return true;
    }
    const tokens = re.exec(s);
    if (tokens) {
        const seq = parseInt(tokens[1]);
        const name = tokens[4];
        if (name !== 'Ping' && name !== 'Pong') {
            data.lastSeq = seq;
        }
        const strategy = strategies.get(name);
        if (!strategy) {
            return false;
        }
        const when = new Date(Date.parse(tokens[2]));
        const who = tokens[3];
        const params = tokens.slice(5).filter(p => !!p).map(p => p.replace('\\"', '"').replace('\\\\', '\\'));
        strategy(Origin.remote, data, connection, seq, when, who, ...params);
        if (name !== 'Ping' && name !== 'Pong') {
            if (who !== adminUser) {
                data.user = who;
            }
        }
        if (name == 'Error') {
            return true;
        }
        return false;
    } else {
        if(isEncrypted) {
            console.error('Unable to decrypt', s);
            data.isDecryptError = true;
            return false;
        }
        else {
            console.error('Wrong syntax', s);
            throw new Error('Wrong syntax: ' + s);
        }
    }
}

export async function serializeStrategy(data: Root, date: Date, s: StrategyType, params: string[]): Promise<string> {
    let serialized = '';
    
    const paramsString = params.join('", "');
    const ts = date.toISOString().
        replace('T', ' ').
        replace('Z', '+00:00');     // Browser timestamps are ALWAYS in UTC, i.e. end with Z
    let plainSerialized = `${data.lastSeq}, ${ts}, ${data.user}: ${s.realName}("${paramsString}")`;

    if(useEncryption && s.realName != 'Ping') {
        serialized = await encrypt(plainSerialized);
    }
    else {
        serialized = plainSerialized;
    }

    return serialized;
}
